import {firstValueFrom, takeUntil} from 'rxjs';
import {IBaseCRUDComponent} from '../../../../../sdk/src/components/ibase-crud/ibase-crud.component';
import {
    NewEditServiceRequestModalComponent,
    NewEditServiceRequestModalConfig,
    NewEditServiceRequestModalResult
} from '../../../modals/new-edit-service-request-modal/new-edit-service-request-modal.component';
import {Component, EventEmitter, Input, Output} from '@angular/core';
import {ETableColDataType, ETableColFilterType} from '../../../../../sdk/src/components/generic-table/table-col-def';
import {PrimeNgImportsModule} from '../../../../../sdk/src/prime-ng-imports';
import {EDeleteMode} from '../../../../../sdk/src/enums/delete-mode.enum';
import {
    CpmCGetServiceRequestsParams,
    CpmCServiceRequestApiService
} from '../../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-service-request.api-service';
import {CpmCServiceRequestApiModel} from '../../../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-service-request.api-model';
import {
    NewEditLocationModalComponent,
    NewEditLocationModalConfig
} from '../../../modals/new-edit-location-modal/new-edit-location-modal.component';
import {EDialogOpenMode} from '../../../../../sdk/src/enums/dialog-open-mode.enum';
import {
    NewEditProjectModalComponent,
    NewEditProjectModalConfig
} from '../../../modals/new-edit-project-modal/new-edit-project-modal.component';
import {
    NewEditOrganizationModalComponent,
    NewEditOrganizationModalConfig
} from '../../../modals/new-edit-organization-modal/new-edit-organization-modal.component';
import {CustomerUrlBuilderUtil} from '../../../utils/customer-url-builder.util';
import {SdkImportsModule} from '../../../../../sdk/src/sdk-imports';
import {
    NewEditServiceRequestGroupModalComponent,
    NewEditServiceRequestGroupModalConfig
} from '../../../modals/new-edit-service-request-group-modal/new-edit-service-request-group-modal.component';
import {ServiceRequestStatusDataCellDisplayComponent} from '../../../data-cell-display/service-request-status-data-cell-display/service-request-status-data-cell-display.component';
import {ServiceRequestProgressDataCellDisplayComponent} from '../../../data-cell-display/service-request-progress-data-cell-display/service-request-progress-data-cell-display.component';

/**
 * ServiceRequest CRUD table component
 */
@Component({
    selector: 'app-service-request-crud',
    templateUrl: './service-request-crud.component.html',
    imports: [PrimeNgImportsModule, SdkImportsModule]
})
export class ServiceRequestCRUDComponent extends IBaseCRUDComponent<
    CpmCServiceRequestApiModel,
    NewEditServiceRequestModalComponent,
    NewEditServiceRequestModalConfig,
    NewEditServiceRequestModalResult
> {
    protected helpVisible = false;

    // ===============================================================
    // ===============================================================

    @Input()
    public showOrganization = true;
    @Input()
    public showProject = true;

    @Input()
    public organizationUUID?: string;
    @Input()
    public projectUUID?: string;

    @Output()
    public serviceRequestChanged = new EventEmitter<void>();

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this.configure({
            // Model config
            modelName: 'ServiceRequestApiModel',
            getModelHumanIdentifier: (model: CpmCServiceRequestApiModel) => model.id ?? '<Unknown service request>',

            // Service config
            deleteMode: EDeleteMode.CONFIRM_REENFORCED,
            deleteModelService: (modelToDelete: CpmCServiceRequestApiModel) =>
                CpmCServiceRequestApiService.deleteServiceRequest(modelToDelete.uuid!),

            // Modal config
            modalType: NewEditServiceRequestModalComponent,
            getDialogServiceParameters: NewEditServiceRequestModalComponent.getDialogServiceParameters,
            modalExtendedConfigBuilder: (model?: CpmCServiceRequestApiModel) =>
                new NewEditServiceRequestModalConfig(model, this.projectUUID, this.organizationUUID),

            // Authorization config
            authorizationNew: CpmCServiceRequestApiService.endpointNew(this.FAKE_UUID),
            authorizationEdit: CpmCServiceRequestApiService.endpointEdit(this.FAKE_UUID, this.FAKE_UUID),
            authorizationDelete: CpmCServiceRequestApiService.endpointDelete(this.FAKE_UUID, this.FAKE_UUID)
        });
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    protected override async onReady(): Promise<void> {
        // Subscribe to SDK events and re-emit through our custom @Output
        this.onModalResult.pipe(takeUntil(this._destroy$)).subscribe(() => {
            this.serviceRequestChanged.emit();
        });

        this.onDeleteResult.pipe(takeUntil(this._destroy$)).subscribe(() => {
            this.serviceRequestChanged.emit();
        });
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * On refresh (Error handling is done in the parent)
     */
    public override async onRefresh(): Promise<void> {
        this.loadingStr = 'Loading service requests...';

        const params: CpmCGetServiceRequestsParams = {};

        if (this.organizationUUID) params['organization_uuid'] = this.organizationUUID;
        if (this.projectUUID) params['project_uuid'] = this.projectUUID;

        const result = await firstValueFrom(
            CpmCServiceRequestApiService.getServiceRequestList(params, this.paginatorConfig)
        );

        this.items = result.data;
        this.totalItemCount = result.total;
    }

    // ===============================================================
    // ===============================================================

    /**
     * On help button pressed
     */
    public override async onHelp(): Promise<void> {
        this.helpVisible = true;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Init the columns (Error handling is done in the parent)
     */
    protected override async initColumns(): Promise<void> {
        this.tableCols = [
            {
                field: 'id',
                header: 'ID',
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.TEXT,
                colWidth: '10rem'
            },
            {
                field: 'status',
                header: 'Status',
                dataType: ETableColDataType.DATA_DISPLAY_CELL_COMPONENT,
                dataCellDisplayComponent: ServiceRequestStatusDataCellDisplayComponent,
                searcheable: false,
                filterable: false,
                sortable: false,
                colWidth: '10rem'
            },
            {
                field: 'task_status_cache',
                header: 'Progress',
                dataType: ETableColDataType.DATA_DISPLAY_CELL_COMPONENT,
                dataCellDisplayComponent: ServiceRequestProgressDataCellDisplayComponent,
                searcheable: false,
                filterable: false,
                sortable: false,
                colWidth: '10rem'
            },
            {
                field: 'description',
                header: 'Description',
                dataType: ETableColDataType.LONG_TEXT,
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.TEXT
            },
            {
                field: 'service_request_group.name',
                header: 'Group',
                dataType: ETableColDataType.LINK,
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.TEXT,
                colWidth: '10rem',
                fnOnCellClick: (row: CpmCServiceRequestApiModel) => {
                    this.globalsService.dialogService.open(
                        NewEditServiceRequestGroupModalComponent,
                        NewEditServiceRequestGroupModalComponent.getDialogServiceParameters({
                            dialogOpenMode: EDialogOpenMode.READ_ONLY,
                            extended: {
                                existing: row.service_request_group
                            } as NewEditServiceRequestGroupModalConfig
                        })
                    );
                }
            }
        ];

        if (this.showProject) {
            this.tableCols.push({
                field: 'project.name',
                header: 'Project',
                dataType: ETableColDataType.LINK,
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.TEXT,
                colWidth: '10rem',
                fnOnCellClick: (row: CpmCServiceRequestApiModel) => {
                    this.globalsService.dialogService.open(
                        NewEditProjectModalComponent,
                        NewEditProjectModalComponent.getDialogServiceParameters({
                            dialogOpenMode: EDialogOpenMode.READ_ONLY,
                            extended: {
                                existing: row.project
                            } as NewEditProjectModalConfig,
                            showGoTo: true
                        })
                    );
                }
            });
        }

        if (this.showOrganization) {
            this.tableCols.push({
                field: 'organization.name',
                header: 'Organization',
                dataType: ETableColDataType.LINK,
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.TEXT,
                colWidth: '10rem',
                fnOnCellClick: (row: CpmCServiceRequestApiModel) => {
                    this.globalsService.dialogService.open(
                        NewEditOrganizationModalComponent,
                        NewEditOrganizationModalComponent.getDialogServiceParameters({
                            dialogOpenMode: EDialogOpenMode.READ_ONLY,
                            extended: {
                                existing: row.organization
                            } as NewEditOrganizationModalConfig,
                            showGoTo: true
                        })
                    );
                }
            });
        }

        this.tableCols.push(
            ...[
                {
                    field: 'location.name',
                    header: 'Location',
                    dataType: ETableColDataType.LINK,
                    searcheable: true,
                    filterable: true,
                    filterType: ETableColFilterType.TEXT,
                    colWidth: '10rem',
                    fnOnCellClick: (row: CpmCServiceRequestApiModel) => {
                        this.globalsService.dialogService.open(
                            NewEditLocationModalComponent,
                            NewEditLocationModalComponent.getDialogServiceParameters({
                                dialogOpenMode: EDialogOpenMode.READ_ONLY,
                                extended: {
                                    existing: row.location,
                                    organizationUUID: row.organization_uuid
                                } as NewEditLocationModalConfig,
                                showGoTo: true
                            })
                        );
                    }
                }
            ]
        );

        this.addCreatedColumns();
        this.addCommunicationButtons();

        this.addAccessButtonColumn((row) => {
            const customerUUID = this.globalsService.sessionService.getCustomer()?.uuid;
            this.globalsService.router.navigate(
                [CustomerUrlBuilderUtil.customerServiceRequest(customerUUID, row.uuid)],
                {queryParams: {tab: 'tasks'}}
            );
        }, 'pi pi-list-check');
        this.addAccessButtonColumn((row) => {
            const customerUUID = this.globalsService.sessionService.getCustomer()?.uuid;
            this.globalsService.router.navigate(
                [CustomerUrlBuilderUtil.customerServiceRequest(customerUUID, row.uuid)],
                {queryParams: {tab: 'general'}}
            );
        });
    }
}
